import * as React from "react";
import {
    Avatar,
    Box,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from "@mui/material";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import {useTranslation} from "react-i18next";
import {Contract} from "../../../../services/payrollService";
import {formatEUR} from "../../../../hooks/useDashboard";
import {periodBounds} from "../periodFormat";
import {getAvatarColors, getInitials} from "../../../../utils/avatarUtils";

interface PayrollPeriodEmployeesTableProps {
    contracts: Contract[];
    contractsLoading: boolean;
    contractsPage: number;
    contractsRowsPerPage: number;
    handleContractsChangePage: (event: unknown, newPage: number) => void;
    handleContractsChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
    periodKey: string | null;
}

function isFullMonth(contract: Contract, periodKey: string | null): boolean {
    const bounds = periodBounds(periodKey);
    if (!bounds) return true;
    const start = contract.startDate ? new Date(contract.startDate) : null;
    const end = contract.endDate ? new Date(contract.endDate) : null;
    const startsBeforePeriod = !start || Number.isNaN(start.getTime()) || start <= bounds.start;
    const endsAfterPeriod = !end || Number.isNaN(end.getTime()) || end >= bounds.end;
    return startsBeforePeriod && endsAfterPeriod;
}

export default function PayrollPeriodEmployeesTable({
    contracts,
    contractsLoading,
    contractsPage,
    contractsRowsPerPage,
    handleContractsChangePage,
    handleContractsChangeRowsPerPage,
    periodKey,
}: Readonly<PayrollPeriodEmployeesTableProps>): React.ReactElement {
    const {t} = useTranslation();

    return (
        <>
            <TableContainer sx={{overflowX: "auto"}}>
                <Table sx={{minWidth: 700}} aria-label="payroll period employees table">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('payroll.period.employees.header.employee')}</TableCell>
                            <TableCell>{t('payroll.period.employees.header.contract')}</TableCell>
                            <TableCell>{t('payroll.period.employees.header.basePay')}</TableCell>
                            <TableCell>{t('payroll.period.employees.header.workload')}</TableCell>
                            <TableCell>{t('payroll.period.employees.header.validity')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {contractsLoading ? (
                            Array.from({length: 5}).map((_, i) => (
                                <TableRow key={`employee-skeleton-${i}`}>
                                    <TableCell>
                                        <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                                            <Skeleton variant="circular" width={36} height={36}/>
                                            <Skeleton variant="text" width={120}/>
                                        </Box>
                                    </TableCell>
                                    {Array.from({length: 4}).map((_, j) => (
                                        <TableCell key={j}>
                                            <Skeleton variant="text"/>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : contracts.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{py: 6}}>
                                    <PeopleAltOutlinedIcon sx={{fontSize: 48, color: "text.disabled", mb: 1}}/>
                                    <Typography variant="body1" color="text.secondary">
                                        {t('payroll.period.employees.empty')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            contracts
                                .slice(contractsPage * contractsRowsPerPage, contractsPage * contractsRowsPerPage + contractsRowsPerPage)
                                .map((contract) => {
                                    const avatarColors = getAvatarColors(`${contract.firstName}${contract.lastName}${contract.contractId}`);
                                    return (
                                    <TableRow hover tabIndex={-1} key={contract.contractId}>
                                        <TableCell>
                                            <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                                                <Avatar sx={{
                                                    width: 36,
                                                    height: 36,
                                                    bgcolor: avatarColors.bg,
                                                    color: avatarColors.color,
                                                    fontSize: "0.875rem",
                                                    fontWeight: 600,
                                                }}>
                                                    {getInitials(contract.firstName, contract.lastName)}
                                                </Avatar>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {contract.firstName} {contract.lastName}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{contract.contractNumber}</TableCell>
                                        <TableCell>
                                            {formatEUR(Number(contract.baseAmount))} {contract.salaryBasis}
                                        </TableCell>
                                        <TableCell>{contract.workload}%</TableCell>
                                        <TableCell>
                                            {isFullMonth(contract, periodKey)
                                                ? t('payroll.period.employees.validity.fullMonth')
                                                : t('payroll.period.employees.validity.partial')}
                                        </TableCell>
                                    </TableRow>
                                    );
                                })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {contracts.length > 0 && (
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={contracts.length}
                    rowsPerPage={contractsRowsPerPage}
                    page={contractsPage}
                    onPageChange={handleContractsChangePage}
                    onRowsPerPageChange={handleContractsChangeRowsPerPage}
                />
            )}
        </>
    );
}
